import { useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { SUPPORT_EMAIL } from "@/i18n/content";
import { Reveal } from "@/components/Reveal";
import { Mail, FileText } from "lucide-react";

const PrivacyPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = t.privacy.metaTitle;
  }, [t]);

  return (
    <main data-testid="privacy-page">
      <section className="relative overflow-hidden border-b border-border bg-white">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center gap-3 opacity-[0.05]">
          {[30, 48, 66, 42, 58, 36].map((h, i) => (
            <div key={i} className="w-10 rounded-t-lg bg-primary sm:w-16" style={{ height: `${h}vh` }} />
          ))}
        </div>
        <div className="relative mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-accent px-4 py-1.5 text-xs font-bold tracking-wide text-primary"
            data-testid="privacy-badge"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            {t.privacy.badge}
          </motion.p>
          <h1 className="text-balance text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl" data-testid="privacy-title">
            <span className="mask-line">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {t.privacy.title}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-4 text-sm font-semibold text-muted-foreground"
            data-testid="privacy-updated"
          >
            {t.privacy.updated}
          </motion.p>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20" data-testid="privacy-sections">
        {t.privacy.sections.map((section, i) => (
          <Reveal key={i} delay={0.02}>
            <section className="mb-14" aria-labelledby={`privacy-section-${i + 1}`} data-testid={`privacy-section-${i + 1}`}>
              <div className="flex items-baseline gap-4">
                <span className="shrink-0 text-sm font-extrabold tracking-[0.25em] text-primary/60">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 id={`privacy-section-${i + 1}`} className="text-xl font-bold text-foreground sm:text-2xl">
                  {section.title}
                </h2>
              </div>
              <div className="mt-4 space-y-4 border-s-2 border-primary/20 ps-6">
                {section.paragraphs?.map((p, j) => (
                  <p key={j} className="text-base leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
                {section.list && (
                  <ul className="list-disc space-y-3 ps-5 text-base leading-relaxed text-muted-foreground marker:text-primary">
                    {section.list.map((li, j) => (
                      <li key={j}>{li}</li>
                    ))}
                  </ul>
                )}
                {section.after?.map((p, j) => (
                  <p key={j} className="text-base leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
                {section.email && (
                  <p className="pt-1">
                    <span className="mb-2 block text-sm font-bold text-foreground">
                      {t.contact.emailLabel} / Privacy Contact Email
                    </span>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      dir="ltr"
                      data-testid="privacy-email-link"
                      className="inline-flex items-center gap-2 break-all text-lg font-extrabold text-primary underline-offset-4 hover:underline"
                    >
                      <Mail className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {SUPPORT_EMAIL}
                    </a>
                  </p>
                )}
              </div>
            </section>
          </Reveal>
        ))}
      </div>
    </main>
  );
};

export default PrivacyPage;
